import React, { useEffect, useRef, useState } from 'react';
import { GridView } from '@vivli/shared/views';
import { ColDef, GridApi, ICellRendererParams } from 'ag-grid-community';
import { IEnquiryActionDetailDto } from '@vivli/features/dashboard/infrastructure/interface';
import { EnquiryActionCardView } from '@vivli/features/dashboard/views';
import { multiTypeComparator } from '@vivli/features/dashboard/infrastructure/hook';
import { actionGridContainerStyle } from './actions-grids-styles';

interface EnquiryActionsGridComponentProps {
    onGridReady?: (gridApi: GridApi) => void;
    emptyMessage?: string;
    hideFloatingFilter?: boolean;
    hideHeader?: boolean;
    autoHeight?: boolean;
    dataRefreshing?: boolean;
    dtos?: IEnquiryActionDetailDto[];
    isVivliAdmin?: boolean;
    studiesLabel?: string;
}

export const EnquiryActionsGridComponent = ({
    onGridReady,
    dataRefreshing,
    dtos,
    isVivliAdmin,
    studiesLabel,
}: EnquiryActionsGridComponentProps) => {
    const [rowData, setRowData] = useState<IEnquiryActionDetailDto[]>();
    const gridApiRef = useRef<GridApi>();

    const handleOnGridReady = (gridApi: GridApi) => {
        onGridReady && onGridReady(gridApi);
        gridApiRef.current = gridApi;
    };

    const columnDefs: ColDef[] = [
        {
            field: 'vivliId',
            headerName: 'Enquiry ID',
            filter: true,
            sortable: true,
            valueGetter: (params) => params.data?.vivliId ?? null,
            comparator: multiTypeComparator,
        },
        {
            field: 'numberOfMyStudies',
            headerName: 'Number of My Studies',
            filter: true,
            sortable: true,
            hide: isVivliAdmin,
        },
        {
            field: 'numberOfStudies',
            headerName: 'Total Number of Studies',
            filter: true,
            sortable: true,
            hide: !isVivliAdmin,
        },
        {
            field: 'daysLeft',
            headerName: 'Days in Current Step',
            filter: true,
            sortable: true,
            minWidth: 250,
        },
        {
            field: 'requester',
            headerName: 'Requested By',
            filter: true,
            sortable: true,
        },
        {
            field: 'daysWarning',
            headerName: 'Action Overdue',
            filter: true,
            sortable: true,
            hide: true,
            valueGetter: ({ data }) => (data.daysWarning ? 'Yes' : 'No'),
        },
        {
            field: 'actionText',
            headerName: 'Action Required',
            filter: true,
            sortable: true,
            hide: false,
        },
    ];

    const getFullWidthRenderer = ({ data }: ICellRendererParams<IEnquiryActionDetailDto>) => (
        <EnquiryActionCardView dto={data} isVivliAdmin={isVivliAdmin} studiesLabel={studiesLabel}></EnquiryActionCardView>
    );

    useEffect(() => {
        if ((!dtos || dtos.length === 0) && !dataRefreshing) {
            return;
        }

        setRowData(dtos);
    }, [dtos, dataRefreshing]);

    return (
        <div style={actionGridContainerStyle}>
            <GridView
                rowData={rowData}
                useFullWidthRows={true}
                hideFloatingFilter={false}
                onGridReady={handleOnGridReady}
                fullWidthCellRenderer={getFullWidthRenderer}
                columns={columnDefs}
                rowHeight={80}
            />
        </div>
    );
};
